import { RefObject, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { masks } from "@core/utils/masks";
import { useEditMapIntegrationPlanMutation } from "@modules/mapIntegrationPlans/services/mutations";
import { MapIntegrationPlanFormValues, mapIntegrationPlanSchema } from "@modules/mapIntegrationPlans/validations";
import { listMapIntegrationPlansQueryKey } from "@modules/mapIntegrationPlans/services/queries/listMapIntegrationPlans/query";

export function useEditMapIntegrationPlanModal(modalRef: RefObject<ModalRef>) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<MapIntegrationPlanFormValues>({
    resolver: zodResolver(mapIntegrationPlanSchema({
      partnerPlan: {
        validate: async (hasName, hasList) => hasName || hasList,
        errorMessage: "Informe o nome do plano ou a lista de planos do parceiro",
      }
    })),
    mode: "onBlur",
  });

  const { mutateAsync, isPending } = useEditMapIntegrationPlanMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listMapIntegrationPlansQueryKey,
        refetchType: 'all',
      });

      open?.({
        type: "success",
        message: translate("subscription_mapintegrationplan_admin.success.edit"),
      });

      modalRef.current?.close();
    },
  });

  const handleSubmit = async (data: MapIntegrationPlanFormValues) => {
    const mapintegrationplanId = modalRef.current?.getId();
    await mutateAsync({ id: mapintegrationplanId as string, data });
  }

  // Cria um reset customizado que transforma price_integration antes de popular o formulário
  const customReset = useMemo(() => {
    return (data?: any) => {
      if (data && data.price_integration !== null && data.price_integration !== undefined) {
        // Transforma o valor da API (ex: "123.45") para o formato da máscara (multiplica por 100 e aplica máscara)
        const valueInCents = String(Number(data.price_integration) * 100);
        data = {
          ...data,
          price_integration: masks.currency(valueInCents) as any,
        };
      }
      methods.reset(data);
    };
  }, [methods]);

  return {
    methods: {
      ...methods,
      reset: customReset,
    },
    isPending,
    handleSubmit,
  };
}
